import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { isValid, parseISO, startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { InputAdornment } from '@mui/material';
import { Iconify } from '@pflegenavi/web-components';
import type { ReceiptImageAnalysis } from '@pflegenavi/service-provider-frontend/api';

/** Sparkle marking a value auto-filled by receipt analysis (same icon as expenses). */
export const ReceiptAutofillAdornment = ({
  position = 'start',
}: {
  position?: 'start' | 'end';
}): JSX.Element => (
  <InputAdornment position={position}>
    <Iconify icon="mdi:creation" sx={{ color: 'info.main' }} />
  </InputAdornment>
);

export interface EntryAnalysisValues {
  individualDate: Date | undefined;
  /** First line item amount in cents. */
  amount: number | undefined;
  receiptNumber: string | undefined;
}

export interface UseEntryReceiptAnalysisProps {
  enabled: boolean;
  individualDatesMode: boolean;
  firstImageId: string | undefined;
  values: EntryAnalysisValues;
  analyze: (imageId: string) => Promise<ReceiptImageAnalysis>;
  setValues: (patch: Partial<EntryAnalysisValues>) => void;
}

export interface EntryReceiptAnalysis {
  analyzing: boolean;
  dateAutofilled: boolean;
  amountAutofilled: boolean;
  receiptNumberAutofilled: boolean;
}

interface Autofilled {
  date: Date | null;
  amount: number | null;
  receiptNumber: string | null;
}

const NONE: Autofilled = { date: null, amount: null, receiptNumber: null };

/**
 * Analyzes the first receipt image uploaded to a receipt batch entry and fills
 * the entry date (individual dates mode only), the first line item amount and
 * the receipt number, as long as those fields are still empty. Skips the
 * request when nothing is fillable. Auto-filled values are cleared again when
 * the image is removed, unless the user has edited them since.
 */
export function useEntryReceiptAnalysis({
  enabled,
  individualDatesMode,
  firstImageId,
  values,
  analyze,
  setValues,
}: UseEntryReceiptAnalysisProps): EntryReceiptAnalysis {
  const [analyzing, setAnalyzing] = useState(false);
  const [autofilled, setAutofilled] = useState<Autofilled>(NONE);
  // An image the row mounted with was uploaded earlier and is never analyzed.
  const mountImageIdRef = useRef(firstImageId);
  const analyzedIdRef = useRef<string | undefined>(undefined);
  // A response only applies while its image is still the row's first image;
  // requests for replaced or removed images resolve into nothing.
  const currentImageIdRef = useRef(firstImageId);
  currentImageIdRef.current = firstImageId;
  const { individualDate, amount, receiptNumber } = values;

  const dateAutofilled =
    autofilled.date !== null &&
    individualDate?.getTime() === autofilled.date.getTime();
  const amountAutofilled =
    autofilled.amount !== null && amount === autofilled.amount;
  const receiptNumberAutofilled =
    autofilled.receiptNumber !== null &&
    receiptNumber === autofilled.receiptNumber;

  useEffect(() => {
    if (firstImageId || autofilled === NONE) {
      return;
    }
    const patch: Partial<EntryAnalysisValues> = {};
    if (dateAutofilled) {
      patch.individualDate = undefined;
    }
    if (amountAutofilled) {
      patch.amount = undefined;
    }
    if (receiptNumberAutofilled) {
      patch.receiptNumber = undefined;
    }
    if (Object.keys(patch).length > 0) {
      setValues(patch);
    }
    setAutofilled(NONE);
  }, [
    firstImageId,
    autofilled,
    dateAutofilled,
    amountAutofilled,
    receiptNumberAutofilled,
    setValues,
  ]);

  useEffect(() => {
    if (
      !enabled ||
      !firstImageId ||
      firstImageId === analyzedIdRef.current ||
      firstImageId === mountImageIdRef.current
    ) {
      return;
    }
    const fillDate = individualDatesMode && !individualDate;
    const fillAmount = !amount;
    const fillNumber = !receiptNumber?.trim();
    if (!fillDate && !fillAmount && !fillNumber) {
      return;
    }
    analyzedIdRef.current = firstImageId;
    setAnalyzing(true);
    void (async () => {
      try {
        const result = await analyze(firstImageId);
        if (currentImageIdRef.current !== firstImageId) {
          return;
        }
        const next: Autofilled = { ...NONE };
        const patch: Partial<EntryAnalysisValues> = {};
        const parsedDate = result.receiptDate
          ? parseISO(result.receiptDate)
          : undefined;
        if (fillDate && parsedDate && isValid(parsedDate)) {
          next.date = fromZonedTime(startOfDay(parsedDate), 'UTC');
          patch.individualDate = next.date;
        }
        if (fillAmount && result.total && result.total > 0) {
          next.amount = result.total;
          patch.amount = result.total;
        }
        if (fillNumber && result.receiptNumber) {
          next.receiptNumber = result.receiptNumber;
          patch.receiptNumber = result.receiptNumber;
        }
        if (Object.keys(patch).length > 0) {
          setValues(patch);
        }
        setAutofilled(next);
      } catch {
        // Analysis is best-effort; leave the entry untouched on failure.
      } finally {
        if (analyzedIdRef.current === firstImageId) {
          setAnalyzing(false);
        }
      }
    })();
  }, [
    enabled,
    firstImageId,
    individualDatesMode,
    individualDate,
    amount,
    receiptNumber,
    analyze,
    setValues,
  ]);

  return {
    analyzing,
    dateAutofilled,
    amountAutofilled,
    receiptNumberAutofilled,
  };
}
